'use client';

import { HousePerson, NavArrowDown, User } from '@byway/icons';
import { type CalendarDate, DateFormatter, getLocalTimeZone } from '@internationalized/date';
import { differenceInCalendarDays } from 'date-fns';
import dynamic from 'next/dynamic';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { visuallyHidden } from '~/components/Utilities';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { trackClickEvent, trackFunnelEvent } from '~/utils/funnelTracking';
import { scrollClearOfStickyFooter } from '~/utils/scrollClearOfStickyFooter';
import { useTripDateSelection } from '../hooks/useTripDateSelection';
import { useTripOriginUpdate } from '../hooks/useTripOriginUpdate';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { useTripTravellersUpdate } from '../hooks/useTripTravellersUpdate';
import { ChangeOriginModal } from './ChangeOriginModal';
import { ChangeTravellersModal } from './ChangeTravellersModal';
import { TripDateError } from './TripDateError';

const RangeCalendar = dynamic(() => import('~/components/RangeCalendar').then((m) => m.RangeCalendar), {
  ssr: false
});

import styles from './BookingDetails.module.css';

export function BookingDetails() {
  const t = useTranslations('journey');
  const tAria = useTranslations('aria');
  const locale = useLocale();
  const timeZone = getLocalTimeZone();
  const isDesktop = useIsDesktop();
  const isTripUpdating = useIsTripUpdating();

  const { tripId, isReadOnly, isDateUnset, isLiveChecking, isStartDateUnavailable } = useTripOverviewSummary();
  const isEditingLocked = isReadOnly || isLiveChecking;
  const isSelectionLocked = isEditingLocked || isDateUnset;
  const {
    setLocalRange,
    range,
    calendarKey,
    hasSelectedRange,
    hasDateChange,
    originalDuration,
    datesConfirmed,
    dateError,
    confirmDates,
    clearDates
  } = useTripDateSelection();
  const { confirmedOrigin: selectedOrigin, confirmedOriginLabel, confirmOrigin } = useTripOriginUpdate();
  const { selectedAdultCount, selectedChildCount, selectedRooms, adultCount, childAges, confirmTravellers } =
    useTripTravellersUpdate();

  const [isOriginModalOpen, setIsOriginModalOpen] = useState(false);
  const [isTravellersModalOpen, setIsTravellersModalOpen] = useState(false);
  const dateActionsRef = useRef<HTMLDivElement>(null);

  const detailFormatter = new DateFormatter(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  const dateRangeStart = detailFormatter.format(range.start.toDate(timeZone));
  const dateRangeEnd = detailFormatter.format(range.end.toDate(timeZone));

  const tripDayCount = differenceInCalendarDays(range.end.toDate(timeZone), range.start.toDate(timeZone)) + 1;

  return (
    <section id="overview-booking-details" className={styles.section} aria-labelledby="trip-v2-booking-heading">
      <h2 id="trip-v2-booking-heading" className={styles.heading}>
        {isDesktop ? t('edit_dates_your') : t('booking_details_heading')}
      </h2>

      <div className={styles.desktopLayout}>
        <div className={styles.stack}>
          <div className={`${styles.group} ${styles.desktopHidden}`}>
            <p className={styles.question}>{t('booking_origin_question')}</p>
            <button
              type="button"
              className={styles.selectRow}
              aria-label={t('booking_choose_origin')}
              disabled={isSelectionLocked}
              onClick={() => {
                trackClickEvent('trip-page-edit-click-origin');
                setIsOriginModalOpen(true);
              }}
            >
              <span className={styles.rowIcon} aria-hidden>
                <HousePerson decorative size="large" />
              </span>
              <span className={styles.rowValue}>{confirmedOriginLabel}</span>
            </button>
          </div>

          <div className={`${styles.divider} ${styles.desktopHidden}`} aria-hidden />

          <div className={styles.group} id="overview-booking-dates">
            <p className={`${styles.question} ${styles.desktopDatesQuestionHidden}`}>{t('booking_dates_question')}</p>
            <p className={styles.tripLength}>{t('booking_trip_day_count', { count: tripDayCount })}</p>
            {hasSelectedRange && (
              <p className={styles.dateDetail}>
                {dateRangeStart}
                <span aria-hidden="true"> → </span>
                <span className={visuallyHidden}>{` ${tAria('to')} `}</span>
                {dateRangeEnd}
              </p>
            )}
            {dateError && <TripDateError message={dateError} />}
            <div className={styles.calendar}>
              <RangeCalendar
                key={calendarKey}
                aria-label={t('booking_dates_calendar_label')}
                monthsToShow={isDesktop ? 2 : 1}
                layout={isDesktop ? 'horizontal' : 'vertical'}
                variant="connected"
                value={hasSelectedRange ? range : null}
                isDisabled={isEditingLocked}
                fixedDuration={originalDuration ?? undefined}
                isStartDateUnavailable={isStartDateUnavailable}
                onChange={(next) => {
                  if (!next?.start || !next.end) return;
                  trackFunnelEvent('trip_funnel_dates_calendar_select');
                  const start = next.start as CalendarDate;
                  const end =
                    originalDuration != null ? start.add({ days: originalDuration - 1 }) : (next.end as CalendarDate);
                  setLocalRange({ start, end });
                  if (!isDesktop) {
                    requestAnimationFrame(() => {
                      if (dateActionsRef.current) scrollClearOfStickyFooter(dateActionsRef.current);
                    });
                  }
                }}
              />
            </div>
            {!isReadOnly && (
              <div className={styles.dateActions} ref={dateActionsRef} id="overview-booking-date-actions">
                <button type="button" className={styles.clearDates} onClick={clearDates}>
                  {t('booking_clear_dates')}
                </button>
                {(hasDateChange || (!datesConfirmed && !isDateUnset)) && (
                  <Button
                    variant="secondary"
                    isPillShape
                    size="small"
                    onPress={() => {
                      trackFunnelEvent('trip_funnel_confirm_dates', { location: 'booking_details' });
                      void confirmDates();
                    }}
                    isDisabled={isTripUpdating}
                  >
                    {hasDateChange ? t('booking_save_changes') : t('continue')}
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className={`${styles.divider} ${styles.desktopHidden}`} aria-hidden />

          <div className={`${styles.group} ${styles.desktopHidden}`}>
            <p className={styles.question}>{t('booking_travellers_question')}</p>
            <button
              type="button"
              className={styles.selectRow}
              aria-label={t('booking_choose_travellers')}
              disabled={isSelectionLocked}
              onClick={() => {
                trackClickEvent('trip-page-edit-click-pax');
                setIsTravellersModalOpen(true);
              }}
            >
              <span className={styles.rowIcon} aria-hidden>
                <User decorative size="large" />
              </span>
              <span className={styles.rowValue}>
                {t('booking_travellers_summary', {
                  adults: selectedAdultCount,
                  children: selectedChildCount,
                  rooms: selectedRooms
                })}
              </span>
              <span className={styles.rowChevron} aria-hidden>
                <NavArrowDown decorative size="small" />
              </span>
            </button>
          </div>
        </div>

        <aside className={styles.desktopPricingBox}>
          <h3 className={styles.desktopPricingTitle}>{t('overview_pricing_heading')}</h3>
          <p className={styles.desktopPricingText}>{t('overview_pricing_description_1')}</p>
        </aside>
      </div>
      <div className={styles.divider} aria-hidden />
      <ChangeOriginModal
        isOpen={isOriginModalOpen}
        close={() => setIsOriginModalOpen(false)}
        currentOrigin={selectedOrigin}
        tripId={tripId}
        onConfirm={confirmOrigin}
      />
      <ChangeTravellersModal
        key={`${adultCount}-${childAges.join(',')}-${selectedRooms}`}
        isOpen={isTravellersModalOpen}
        close={() => setIsTravellersModalOpen(false)}
        adultCount={adultCount}
        initialChildAges={childAges}
        rooms={selectedRooms}
        tripEndDateLabel={dateRangeEnd}
        onConfirm={confirmTravellers}
      />
    </section>
  );
}
